import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NoteRow, useEstablishmentNotes } from "@/hooks/useClientFollowup";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  establishmentId: string;
  note?: NoteRow | null;
}

export function NoteDialog({ open, onOpenChange, establishmentId, note }: Props) {
  const { save } = useEstablishmentNotes(establishmentId);
  const [form, setForm] = useState({
    note_datetime: new Date().toISOString().slice(0, 16),
    note_type: "call",
    title: "",
    content: "",
    next_action: "",
    next_action_date: "",
  });

  useEffect(() => {
    if (note) {
      setForm({
        note_datetime: note.note_datetime.slice(0, 16),
        note_type: note.note_type,
        title: note.title,
        content: note.content ?? "",
        next_action: note.next_action ?? "",
        next_action_date: note.next_action_date ?? "",
      });
    } else {
      setForm({
        note_datetime: new Date().toISOString().slice(0, 16),
        note_type: "call",
        title: "",
        content: "",
        next_action: "",
        next_action_date: "",
      });
    }
  }, [note, open]);

  const set = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    await save.mutateAsync({
      ...(note?.id ? { id: note.id } : {}),
      note_datetime: new Date(form.note_datetime).toISOString(),
      note_type: form.note_type as any,
      title: form.title,
      content: form.content || null,
      next_action: form.next_action || null,
      next_action_date: form.next_action_date || null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{note ? "Modifier la note" : "Nouvelle note"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date & heure</Label>
              <Input type="datetime-local" value={form.note_datetime} onChange={(e) => set("note_datetime", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.note_type} onValueChange={(v) => set("note_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Appel</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="visit">Visite</SelectItem>
                  <SelectItem value="meeting">Réunion</SelectItem>
                  <SelectItem value="followup">Suivi</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Titre *</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>Contenu</Label>
            <Textarea rows={5} value={form.content} onChange={(e) => set("content", e.target.value)} />
          </div>

          <div className="border-t pt-4 space-y-4">
            <div className="space-y-2">
              <Label>Prochaine action à mener</Label>
              <Input value={form.next_action} onChange={(e) => set("next_action", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Date de la prochaine action</Label>
              <Input type="date" value={form.next_action_date} onChange={(e) => set("next_action_date", e.target.value)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={save.isPending || !form.title}>
            {save.isPending ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
